import React, { useCallback, useState } from 'react';
import { useDropzone, Accept } from 'react-dropzone';
import { CloudArrowUpIcon, DocumentIcon, ArchiveBoxIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { FileWithPreview, FileCategory, SupportedFormatsResponse } from '../../types/uploads.types';

interface DropZoneProps {
  onFilesSelected: (files: FileWithPreview[]) => void;
  supportedFormats?: SupportedFormatsResponse;
  disabled?: boolean;
  maxFiles?: number;
}

const getFileCategory = (file: File, formats?: SupportedFormatsResponse): FileCategory => {
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();

  if (formats) {
    if (formats.formats.audio.extensions.includes(ext)) return 'audio';
    if (formats.formats.archive.extensions.includes(ext)) return 'archive';
    if (formats.formats.cdr.extensions.includes(ext)) return 'cdr';
  } else {
    // Fallback detection
    if (['.wav', '.mp3', '.m4a', '.flac', '.ogg'].includes(ext)) return 'audio';
    if (['.zip', '.tar', '.tar.gz', '.tgz'].includes(ext)) return 'archive';
    if (['.csv', '.xml', '.json'].includes(ext)) return 'cdr';
  }

  return 'unknown';
};

const getCategoryIcon = (category: FileCategory) => {
  switch (category) {
    case 'audio':
      return <DocumentIcon className="h-5 w-5 text-indigo-500" />;
    case 'archive':
      return <ArchiveBoxIcon className="h-5 w-5 text-amber-500" />;
    case 'cdr':
      return <TableCellsIcon className="h-5 w-5 text-green-500" />;
    default:
      return <DocumentIcon className="h-5 w-5 text-gray-400" />;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const DropZone: React.FC<DropZoneProps> = ({
  onFilesSelected,
  supportedFormats,
  disabled = false,
  maxFiles = 100,
}) => {
  const { t } = useTranslation('uploads');
  const [isDragActive, setIsDragActive] = useState(false);

  const getAllExtensions = (): string[] => {
    if (supportedFormats) {
      return [
        ...supportedFormats.formats.audio.extensions,
        ...supportedFormats.formats.archive.extensions,
        ...supportedFormats.formats.cdr.extensions,
      ];
    }
    return ['.wav', '.mp3', '.m4a', '.flac', '.ogg', '.zip', '.tar', '.tar.gz', '.tgz', '.csv', '.xml', '.json'];
  };

  const accept: Accept = {
    'audio/*': getAllExtensions().filter(ext => ['.wav', '.mp3', '.m4a', '.flac', '.ogg'].includes(ext)),
    'application/zip': ['.zip'],
    'application/x-tar': ['.tar'],
    'application/gzip': ['.tar.gz', '.tgz'],
    'text/csv': ['.csv'],
    'application/xml': ['.xml'],
    'application/json': ['.json'],
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const filesWithPreview: FileWithPreview[] = acceptedFiles.map((file) => ({
      file,
      id: `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      status: 'pending' as const,
      category: getFileCategory(file, supportedFormats),
    }));

    onFilesSelected(filesWithPreview);
  }, [onFilesSelected, supportedFormats]);

  const { getRootProps, getInputProps, isDragReject } = useDropzone({
    onDrop,
    accept,
    maxFiles,
    disabled,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
  });

  const maxSizeMB = supportedFormats?.limits.max_single_file_mb || 50;
  const maxArchiveMB = supportedFormats?.limits.max_archive_mb || 500;

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
          transition-all duration-200 ease-in-out
          ${disabled ? 'bg-gray-50 border-gray-200 cursor-not-allowed' : ''}
          ${isDragActive && !isDragReject ? 'border-indigo-500 bg-indigo-50' : ''}
          ${isDragReject ? 'border-red-500 bg-red-50' : ''}
          ${!isDragActive && !isDragReject && !disabled ? 'border-gray-300 hover:border-indigo-400 hover:bg-gray-50' : ''}
        `}
      >
        <input {...getInputProps()} />

        <div className="flex flex-col items-center">
          <CloudArrowUpIcon
            className={`h-12 w-12 mb-4 ${
              isDragActive && !isDragReject
                ? 'text-indigo-500'
                : isDragReject
                ? 'text-red-500'
                : 'text-gray-400'
            }`}
          />

          {isDragActive && !isDragReject ? (
            <p className="text-lg font-medium text-indigo-600">
              {t('webUpload.dropHere')}
            </p>
          ) : isDragReject ? (
            <p className="text-lg font-medium text-red-600">
              {t('webUpload.invalidFiles')}
            </p>
          ) : (
            <>
              <p className="text-lg font-medium text-gray-700">
                {t('webUpload.dropZone')}
              </p>
              <p className="mt-1 text-sm text-gray-500">
                {t('webUpload.orClick')}
              </p>
            </>
          )}
        </div>

        {/* Format badges */}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-medium text-indigo-700">
            <DocumentIcon className="mr-1 h-3.5 w-3.5" />
            {t('webUpload.formats.audio')}
          </span>
          <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
            <ArchiveBoxIcon className="mr-1 h-3.5 w-3.5" />
            {t('webUpload.formats.archive')}
          </span>
          <span className="inline-flex items-center rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
            <TableCellsIcon className="mr-1 h-3.5 w-3.5" />
            {t('webUpload.formats.cdr')}
          </span>
        </div>

        {/* Size limits */}
        <p className="mt-4 text-xs text-gray-400">
          {t('webUpload.sizeLimits', { maxFile: maxSizeMB, maxArchive: maxArchiveMB })}
        </p>
      </div>

      {/* Supported formats detail */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wider mb-3">
          {t('webUpload.supportedFormats')}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="flex items-center text-gray-700 font-medium mb-1">
              {getCategoryIcon('audio')}
              <span className="ml-1">{t('webUpload.formatCategories.audio')}</span>
            </div>
            <p className="text-gray-500 text-xs">WAV, MP3, M4A, FLAC, OGG</p>
          </div>
          <div>
            <div className="flex items-center text-gray-700 font-medium mb-1">
              {getCategoryIcon('archive')}
              <span className="ml-1">{t('webUpload.formatCategories.archive')}</span>
            </div>
            <p className="text-gray-500 text-xs">ZIP, tar.gz, tgz, tar</p>
          </div>
          <div>
            <div className="flex items-center text-gray-700 font-medium mb-1">
              {getCategoryIcon('cdr')}
              <span className="ml-1">{t('webUpload.formatCategories.cdr')}</span>
            </div>
            <p className="text-gray-500 text-xs">CSV, XML, JSON</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export { getCategoryIcon, formatFileSize };
export default DropZone;
